import { shallowRef } from "vue";

import Events from "./events";
import { useEventListener } from "@vueuse/core";

export type Keys = keyof typeof Events;
type DataOf<K extends Keys> = (typeof Events)[K]["data"];

type EntryRef<K extends Keys> = { set: boolean; data?: DataOf<K> };

class Entry<K extends Keys> {
  private readonly ref = shallowRef<EntryRef<K>>({ set: false });
  private readonly resolvers: ((data: DataOf<K>) => void)[] = [];

  public receive(data: DataOf<K>) {
    this.ref.value = { set: true, data };
    this.resolvers.forEach((r) => r(data));
    this.resolvers.length = 0;
  }

  public async wait(): Promise<DataOf<K>> {
    const { set, data } = this.ref.value;
    if (set) {
      return data as DataOf<K>;
    }

    // TODO: add timeout?
    return new Promise<DataOf<K>>((resolve) => {
      this.resolvers.push(resolve);
    });
  }
}

export class BusEvent<K extends Keys> extends CustomEvent<DataOf<K>> {
  constructor(type: K, detail: DataOf<K>) {
    super(type as string, { detail });
  }
}

class EventBus extends EventTarget {
  private readonly cache = new Map<Keys, Entry<Keys>>();

  constructor(private readonly definition: typeof Events) {
    super();
  }

  public once<K extends Keys>(event: K): Promise<DataOf<K>> {
    return this.entryFor(event).wait();
  }

  public useEventListener<K extends Keys>(
    event: K,
    listener: (ev: DataOf<K>) => void,
    options?: AddEventListenerOptions
  ) {
    return useEventListener(
      this,
      event,
      (ev: BusEvent<K>) => listener(ev.detail),
      options
    );
  }

  public emit<K extends Keys>(event: K, data: DataOf<K>): void {
    this.entryFor(event).receive(data);
    this.dispatchEvent(new BusEvent(event, data));
  }

  private entryFor<K extends Keys>(event: K): Entry<K> {
    let entry: Entry<K>;
    if (!this.cache.has(event)) {
      entry = new Entry<K>();
      this.cache.set(event, entry);
    }
    return entry || (this.cache.get(event)! as Entry<K>);
  }
}

export default new EventBus(Events);
